import { createRouter } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen';

/**
 * URLSearchParams-based search parser/stringifier.
 *
 * TanStack Router's default JSON-encodes scalar values (e.g. `?incomplete=1` becomes
 * the number `1`, `?org=a` becomes the string `"a"` after wrapping in quotes during
 * write). nuqs (used in `useAssetFilters`) expects plain string values and
 * comma-separated arrays. Without aligning the two, filter URLs do not round-trip
 * through TanStack-driven navigation and nuqs reads back the JSON-encoded form
 * unchanged. We standardise on the plain-string format used by nuqs.
 *
 * - Parse: every value comes back as a string. Repeated keys are not used; the last
 *   wins. nuqs encodes arrays comma-separated, so `?org=a,b` stays one string and
 *   `pickFilterSearch` (and nuqs `parseAsArrayOf`) split on the comma when needed.
 * - Stringify: arrays are joined with commas; nullish/empty values are dropped;
 *   everything else is coerced to its string form.
 */
function parseSearch(search: string): Record<string, unknown> {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  for (const [key, value] of params) {
    result[key] = value;
  }
  return result;
}

function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      const joined = value
        .filter(
          (v): v is string | number | boolean =>
            typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean',
        )
        .map((v) => String(v))
        .join(',');
      if (joined.length > 0) params.set(key, joined);
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      params.set(key, String(value));
    }
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultViewTransition: true,
  parseSearch,
  stringifySearch,
  context: {
    queryClient: undefined!,
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
